import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Checkbox, Collapse, Drawer, Dropdown, Empty, Input, List, Modal, Popconfirm, Select, Space, Tag, Typography, message } from "antd";
import { DeleteOutlined, EditOutlined, SettingOutlined } from "@ant-design/icons";
import { PlusOutlined, MessageOutlined } from "@ant-design/icons";
import { AgentChat } from "@lude/agents-im";
import "@lude/agents-im/theme/tokens.css";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/auth";
import { useWsStore } from "@/stores/ws";
import { useThemeStore } from "@/stores/theme";
import { useStatusBarStore } from "@/stores/statusbar";
import { agentService, type AgentInfo } from "@/services/agent";
import { sessionService, type SessionInfo } from "@/services/session";
import { memoryService, type MemoryItem } from "@/services/memory";
import { orgService } from "@/services/organization";
import { webTransport } from "@/services/webTransport";
import { api } from "@/services/api";
import type { OrgInfo } from "@/stores/management";
import type { AgentMeta, SessionMeta, AdapterType } from "@lude/agents-im";

const { Text } = Typography;

const statusColor = (s: string) =>
  s === "running" ? "green" : s === "busy" ? "orange" : "default";

// ─── Main Workspace ───

export function AgentWorkspace() {
  const { t } = useTranslation();
  const { connected, connect, subscribeAll, subscribeSession } = useWsStore();
  const { setSessionContext, clearSessionContext } = useStatusBarStore();
  const { isDark } = useThemeStore();
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [sessions, setSessions] = useState<Map<string, SessionInfo[]>>(
    new Map(),
  );
  const [activeAgent, setActiveAgent] = useState<AgentInfo | null>(null);
  const [activeSession, setActiveSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(
    new Map(),
  );

  useEffect(() => {
    if (!connected) connect();
  }, [connected, connect]);

  // ─── Task 2: Listen for global WS events to update title / status ───
  useEffect(() => {
    const unsub = subscribeAll((data: any) => {
      const sessionId: string | undefined = data?.session_id;
      if (!sessionId) return;

      if (data._type === "title_update" && data.title) {
        // Update session title in sidebar list
        setSessions((prev) => {
          const next = new Map(prev);
          for (const [agentId, list] of next) {
            const idx = list.findIndex((s) => s.id === sessionId);
            if (idx !== -1) {
              const updated = [...list];
              updated[idx] = { ...updated[idx], title: data.title };
              next.set(agentId, updated);
              break;
            }
          }
          return next;
        });
        // Also update activeSession if it matches
        setActiveSession((prev) =>
          prev?.id === sessionId ? { ...prev, title: data.title } : prev,
        );
      }

      // Status changes: detect from event type or event presence
      if (data.event) {
        // AgentEvent uses externally-tagged serde: {"Started": null}, {"Exited": {...}}, {"TextDelta": {...}}
        const event = data.event;
        const eventType: string = typeof event === "string" ? event
          : event.type ?? event._type ?? Object.keys(event)[0] ?? "";
        let newStatus: string | null = null;

        if (eventType === "Exited") {
          newStatus = "idle";
        } else if (eventType === "Started" || eventType === "TextDelta" || eventType === "ThinkingDelta") {
          newStatus = "running";
        }

        if (newStatus) {
          setSessions((prev) => {
            const next = new Map(prev);
            for (const [agentId, list] of next) {
              const idx = list.findIndex((s) => s.id === sessionId);
              if (idx !== -1) {
                const updated = [...list];
                updated[idx] = { ...updated[idx], status: newStatus! };
                next.set(agentId, updated);
                break;
              }
            }
            return next;
          });
          setActiveSession((prev) =>
            prev?.id === sessionId ? { ...prev, status: newStatus! } : prev,
          );
        }
      }
    });

    return unsub;
  }, [subscribeAll]);

  useEffect(() => {
    orgService.list().then((list) => {
      setOrgs(list);
      if (list.length > 0 && !selectedOrg) setSelectedOrg(list[0].id);
    });
  }, []);

  const autoSubRef = useRef<(() => void)[]>([]);
  useEffect(() => {
    if (!selectedOrg) return;
    setLoading(true);
    agentService
      .list(selectedOrg)
      .then((list) => {
        setAgents(list);
        Promise.all(
          list.map(
            (a) =>
              sessionService.list(a.id).then((s) => [a.id, s] as const),
          ),
        ).then((results) => {
          const map = new Map<string, SessionInfo[]>();
          for (const [agentId, sessionList] of results)
            map.set(agentId, sessionList);
          setSessions(map);

          // Auto-subscribe to all running/busy sessions
          autoSubRef.current.forEach((u) => u());
          autoSubRef.current = [];
          for (const [, sessionList] of results) {
            for (const s of sessionList) {
              if (s.status === "running" || s.status === "busy") {
                autoSubRef.current.push(subscribeSession(s.id, () => {}));
              }
            }
          }
        });
      })
      .finally(() => setLoading(false));
    return () => { autoSubRef.current.forEach((u) => u()); autoSubRef.current = []; };
  }, [selectedOrg]);

  const [createModalAgent, setCreateModalAgent] = useState<AgentInfo | null>(null);
  const [newSessionTitle, setNewSessionTitle] = useState("");

  const handleCreateSession = (agent: AgentInfo) => {
    setCreateModalAgent(agent);
    setNewSessionTitle("");
  };

  const confirmCreateSession = async () => {
    if (!createModalAgent) return;
    const title = newSessionTitle.trim() || "New Session";
    try {
      const session = await sessionService.create(createModalAgent.id, title);
      setSessions((prev) => {
        const next = new Map(prev);
        next.set(createModalAgent.id, [session, ...(next.get(createModalAgent.id) || [])]);
        return next;
      });
      setActiveAgent(createModalAgent);
      setActiveSession(session);
      setCreateModalAgent(null);
    } catch (e) {
      message.error("Failed to create session: " + (e instanceof Error ? e.message : "Unknown error"));
    }
  };

  // ─── Session rename / delete ───
  const [renameSession, setRenameSession] = useState<SessionInfo | null>(null);
  const [renameTitle, setRenameTitle] = useState("");

  const handleRenameSession = async () => {
    if (!renameSession || !renameTitle.trim()) return;
    try {
      await sessionService.update(renameSession.id, { title: renameTitle.trim() });
      setSessions((prev) => {
        const next = new Map(prev);
        for (const [aid, list] of next) {
          const idx = list.findIndex((s) => s.id === renameSession.id);
          if (idx !== -1) {
            const updated = [...list];
            updated[idx] = { ...updated[idx], title: renameTitle.trim() };
            next.set(aid, updated);
            break;
          }
        }
        return next;
      });
      setActiveSession((prev) =>
        prev?.id === renameSession.id ? { ...prev, title: renameTitle.trim() } : prev
      );
      setRenameSession(null);
    } catch { message.error(t("error")); }
  };

  const handleDeleteSession = async (s: SessionInfo) => {
    try {
      await sessionService.delete(s.id);
      setSessions((prev) => {
        const next = new Map(prev);
        for (const [aid, list] of next) {
          next.set(aid, list.filter((ss) => ss.id !== s.id));
        }
        return next;
      });
      if (activeSession?.id === s.id) { setActiveSession(null); setActiveAgent(null); }
      message.success(t("success"));
    } catch { message.error(t("error")); }
  };

  // ─── Agent config drawer (memory + extensions) ───
  const [drawerAgent, setDrawerAgent] = useState<AgentInfo | null>(null);
  const [canEdit, setCanEdit] = useState(false);

  // Memory state
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [editingMemory, setEditingMemory] = useState<{ id?: string; content: string } | null>(null);
  const [memorySaving, setMemorySaving] = useState(false);

  // Extension state
  const [myMcps, setMyMcps] = useState<any[]>([]);
  const [mySkills, setMySkills] = useState<any[]>([]);
  const [boundMcpIds, setBoundMcpIds] = useState<string[]>([]);
  const [boundSkillIds, setBoundSkillIds] = useState<string[]>([]);
  const [extSaving, setExtSaving] = useState(false);

  const openDrawer = async (agent: AgentInfo) => {
    setDrawerAgent(agent);
    setEditingMemory(null);

    const [mems, allMcps, allSkills, agentMcps, agentSkills, perms] = await Promise.all([
      memoryService.list(agent.id).catch(() => []),
      api.get<any[]>("/my-mcps").catch(() => []),
      api.get<any[]>("/my-skills").catch(() => []),
      api.get<any[]>(`/agents/${agent.id}/my-mcps`).catch(() => []),
      api.get<any[]>(`/agents/${agent.id}/my-skills`).catch(() => []),
      selectedOrg ? orgService.myPermissions(selectedOrg).catch((): string[] => []) : Promise.resolve([] as string[]),
    ]);
    setMemories(mems);
    setMyMcps(allMcps);
    setMySkills(allSkills);
    setBoundMcpIds(agentMcps.map((m: any) => m.id));
    setBoundSkillIds(agentSkills.map((s: any) => s.id));
    setCanEdit(perms.includes("AgentUpdate"));
  };

  const handleSaveMemory = async () => {
    if (!drawerAgent || !editingMemory || !editingMemory.content.trim()) return;
    setMemorySaving(true);
    try {
      if (editingMemory.id) {
        const updated = await memoryService.update(drawerAgent.id, editingMemory.id, editingMemory.content.trim());
        setMemories((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      } else {
        const created = await memoryService.create(drawerAgent.id, editingMemory.content.trim());
        setMemories((prev) => [...prev, created]);
      }
      setEditingMemory(null);
      message.success(t("success"));
    } catch { message.error(t("error")); }
    setMemorySaving(false);
  };

  const handleDeleteMemory = async (memId: string) => {
    if (!drawerAgent) return;
    try {
      await memoryService.delete(drawerAgent.id, memId);
      setMemories((prev) => prev.filter((m) => m.id !== memId));
      message.success(t("success"));
    } catch { message.error(t("error")); }
  };

  const saveExtensions = async () => {
    if (!drawerAgent) return;
    setExtSaving(true);
    try {
      const [currentMcps, currentSkills] = await Promise.all([
        api.get<any[]>(`/agents/${drawerAgent.id}/my-mcps`).catch(() => []),
        api.get<any[]>(`/agents/${drawerAgent.id}/my-skills`).catch(() => []),
      ]);
      const oldMcpIds = currentMcps.map((m: any) => m.id);
      const oldSkillIds = currentSkills.map((s: any) => s.id);
      await Promise.all([
        ...oldMcpIds.filter((id: string) => !boundMcpIds.includes(id)).map((id: string) =>
          api.delete(`/agents/${drawerAgent.id}/my-mcps/unbind/${id}`)),
        ...boundMcpIds.filter((id) => !oldMcpIds.includes(id)).map((id) =>
          api.post(`/agents/${drawerAgent.id}/my-mcps/bind`, { mcp_id: id })),
        ...oldSkillIds.filter((id: string) => !boundSkillIds.includes(id)).map((id: string) =>
          api.delete(`/agents/${drawerAgent.id}/my-skills/unbind/${id}`)),
        ...boundSkillIds.filter((id) => !oldSkillIds.includes(id)).map((id) =>
          api.post(`/agents/${drawerAgent.id}/my-skills/bind`, { skill_id: id })),
      ]);
      // Restart all running sessions under this agent to pick up new MCP/Skill
      const agentSessions = sessions.get(drawerAgent.id) || [];
      for (const s of agentSessions) {
        if (s.status === "running" || s.status === "busy") {
          await sessionService.kill(s.id).catch(() => {});
          await sessionService.spawn(s.id).catch(() => {});
          webTransport.sendMessage(drawerAgent.id, s.id,
            "MCP/SKILL列表已更新，请继续完成刚刚的工作。");
        }
      }
      message.success(t("success"));
    } catch { message.error(t("error")); }
    setExtSaving(false);
  };

  const toggleMcp = (id: string) => {
    setBoundMcpIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSkill = (id: string) => {
    setBoundSkillIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectSession = (agent: AgentInfo, session: SessionInfo) => {
    setActiveAgent(agent);
    setActiveSession(session);
    setSessionAdapterOverride(null);
    // Clear unread when selecting a session
    setUnreadCounts((prev) => {
      if (!prev.has(session.id)) return prev;
      const next = new Map(prev);
      next.delete(session.id);
      return next;
    });
  };

  // ─── Task 4: Unread change handler ───
  const handleUnreadChange = useCallback(
    (state: {
      counts: Record<string, number>;
      activity: Record<string, boolean>;
    }) => {
      setUnreadCounts((prev) => {
        const next = new Map(prev);
        for (const [sid, count] of Object.entries(state.counts)) {
          // Only track unread for sessions that are NOT the active one
          if (sid === activeSession?.id) continue;
          if (count > 0) {
            next.set(sid, count);
          } else {
            next.delete(sid);
          }
        }
        return next;
      });
    },
    [activeSession?.id],
  );

  const agentMeta: AgentMeta | null = activeAgent
    ? {
        name: activeAgent.name,
        adapterType: activeAgent.adapter_type as AdapterType,
        contextWindow: activeAgent.context_window,
      }
    : null;

  // Session-level adapter type: from API response or model switch override
  const [sessionAdapterOverride, setSessionAdapterOverride] = useState<string | null>(null);

  const effectiveAdapterType = sessionAdapterOverride
    || activeSession?.adapter_type
    || activeAgent?.adapter_type || "";

  const sessionMeta: SessionMeta | null = activeSession
    ? {
        sessionModel: activeSession.model,
        sessionProviderId: activeSession.provider_id || undefined,
        sessionAdapterType: effectiveAdapterType as AdapterType || undefined,
      }
    : null;

  const handleSwitchModelSuccess = useCallback(
    (sessionId: string, adapterType: string, providerId: string, model: string) => {
      setActiveSession((prev) =>
        prev?.id === sessionId
          ? { ...prev, model, provider_id: providerId, status: "idle" }
          : prev,
      );
      setSessionAdapterOverride(adapterType);
      setSessions((prev) => {
        const next = new Map(prev);
        for (const [agentId, list] of next) {
          const idx = list.findIndex((s) => s.id === sessionId);
          if (idx !== -1) {
            const updated = [...list];
            updated[idx] = { ...updated[idx], model, provider_id: providerId, status: "idle" };
            next.set(agentId, updated);
            break;
          }
        }
        return next;
      });
    },
    [],
  );

  const allSessionIds = useMemo(() => {
    const ids: string[] = [];
    sessions.forEach((list) => list.forEach((s) => ids.push(s.id)));
    return ids;
  }, [sessions]);

  useEffect(() => {
    if (activeAgent && activeSession) {
      setSessionContext(effectiveAdapterType, activeSession.model, activeSession.status);
    } else {
      clearSessionContext();
    }
    return () => clearSessionContext();
  }, [activeAgent?.adapter_type, activeSession?.model, activeSession?.status]);

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 240,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid var(--border, #E5E7EB)",
          background: "var(--sider-bg, #fff)",
        }}
      >
        <div style={{ padding: "12px 12px 8px" }}>
          <Select
            value={selectedOrg || undefined}
            onChange={setSelectedOrg}
            style={{ width: "100%" }}
            placeholder="Select Organization"
            options={orgs.map((o) => ({ label: o.name, value: o.id }))}
            size="small"
          />
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "0 8px" }}>
          {agents.map((agent) => (
            <div key={agent.id} style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "4px 4px",
                }}
              >
                <Text strong style={{ fontSize: 13 }}>
                  {agent.name}
                </Text>
                <Space size={0}>
                  <Button type="text" size="small" icon={<SettingOutlined />}
                    onClick={() => openDrawer(agent)} title={t("drawer.title")} />
                  <Button type="text" size="small" icon={<PlusOutlined />}
                    onClick={() => handleCreateSession(agent)} />
                </Space>
              </div>
              <List
                size="small"
                dataSource={sessions.get(agent.id) || []}
                renderItem={(s: SessionInfo) => {
                  const isActive = activeSession?.id === s.id;
                  const unread = unreadCounts.get(s.id) ?? 0;
                  return (
                    <Dropdown
                      trigger={["contextMenu"]}
                      menu={{
                        items: [
                          { key: "rename", icon: <EditOutlined />, label: t("session.rename"), onClick: () => { setRenameSession(s); setRenameTitle(s.title); } },
                          { key: "delete", icon: <DeleteOutlined />, label: t("session.delete"), danger: true, onClick: () => handleDeleteSession(s) },
                        ],
                      }}
                    >
                      <List.Item
                        onClick={() => handleSelectSession(agent, s)}
                        style={{
                          cursor: "pointer",
                          padding: "4px 8px",
                          borderRadius: 4,
                          background: isActive
                            ? "var(--toolbar-active-indicator, #3B8FC4)22"
                            : undefined,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            width: "100%",
                          }}
                        >
                          <MessageOutlined
                            style={{
                              fontSize: 12,
                              color: "var(--text-secondary)",
                            }}
                          />
                          <Text ellipsis style={{ flex: 1, fontSize: 12 }}>
                            {s.title}
                          </Text>
                          {!isActive && unread > 0 && (
                            <Badge
                              count={unread}
                              size="small"
                              style={{ marginRight: 4 }}
                            />
                          )}
                          <Tag
                            color={statusColor(s.status)}
                            style={{ fontSize: 10, margin: 0 }}
                          >
                            {s.status}
                          </Tag>
                        </div>
                      </List.Item>
                    </Dropdown>
                  );
                }}
                locale={{
                  emptyText: (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      No sessions
                    </Text>
                  ),
                }}
              />
            </div>
          ))}
          {!loading && agents.length === 0 && (
            <Empty
              description="No agents available"
              style={{ marginTop: 40 }}
            />
          )}
        </div>
      </div>

      {/* Chat Area + StatusBar */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        className={isDark ? "lude-im lude-im-dark" : "lude-im lude-im-light"}
      >
        {activeSession && activeAgent && agentMeta ? (
          <AgentChat
            transport={webTransport}
            agentId={activeAgent.id}
            sessionId={activeSession.id}
            agent={agentMeta}
            session={sessionMeta || undefined}
            allSessionIds={allSessionIds}
            showModelSwitcher={true}
            onSwitchModelSuccess={handleSwitchModelSuccess}
            onUnreadChange={handleUnreadChange}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Empty description="Select a session to start chatting" />
          </div>
        )}
      </div>

      {/* Agent Config Drawer */}
      <Drawer
        title={drawerAgent ? `${drawerAgent.name} — ${t("drawer.title")}` : ""}
        open={!!drawerAgent} onClose={() => setDrawerAgent(null)} width={420}
      >
        <Collapse defaultActiveKey={["memory", "extensions"]} ghost items={[
          {
            key: "memory",
            label: <Text strong>{t("drawer.memory")}</Text>,
            children: (
              <div>
                {memories.length === 0 && !editingMemory && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("drawer.memory.empty")} />
                )}
                <List size="small" dataSource={memories} renderItem={(mem) => (
                  <List.Item
                    actions={canEdit ? [
                      <Button type="text" size="small" icon={<EditOutlined />}
                        onClick={() => setEditingMemory({ id: mem.id, content: mem.content })} />,
                      <Popconfirm title={t("drawer.memory.delete_confirm")}
                        onConfirm={() => handleDeleteMemory(mem.id)} okText={t("confirm")} cancelText={t("cancel")}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>,
                    ] : undefined}
                  >
                    <Text style={{ fontSize: 12, wordBreak: "break-word" }}>{mem.content}</Text>
                  </List.Item>
                )} />
                {editingMemory ? (
                  <div style={{ marginTop: 8 }}>
                    <Input.TextArea rows={3} value={editingMemory.content}
                      onChange={(e) => setEditingMemory({ ...editingMemory, content: e.target.value })}
                      placeholder={t("drawer.memory.placeholder")} autoFocus />
                    <Space style={{ marginTop: 8 }}>
                      <Button size="small" type="primary" loading={memorySaving}
                        onClick={handleSaveMemory} disabled={!editingMemory.content.trim()}>
                        {t("save")}
                      </Button>
                      <Button size="small" onClick={() => setEditingMemory(null)}>{t("cancel")}</Button>
                    </Space>
                  </div>
                ) : canEdit ? (
                  <Button type="dashed" size="small" icon={<PlusOutlined />} style={{ marginTop: 8 }}
                    onClick={() => setEditingMemory({ content: "" })}>
                    {t("drawer.memory.add")}
                  </Button>
                ) : null}
              </div>
            ),
          },
          {
            key: "extensions",
            label: <Text strong>{t("drawer.extensions")}</Text>,
            extra: <Button type="primary" size="small" loading={extSaving}
              onClick={(e) => { e.stopPropagation(); saveExtensions(); }}>{t("save")}</Button>,
            children: (
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                  {t("drawer.extensions.mcp")}
                </Text>
                {myMcps.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 11 }}>{t("my_mcps.empty")}</Text>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
                    {myMcps.map((m: any) => (
                      <Checkbox key={m.id} checked={boundMcpIds.includes(m.id)}
                        onChange={() => toggleMcp(m.id)}>
                        <span style={{ fontSize: 12 }}>{m.name}</span>
                      </Checkbox>
                    ))}
                  </div>
                )}
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                  {t("drawer.extensions.skill")}
                </Text>
                {mySkills.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 11 }}>{t("my_skills.empty")}</Text>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {mySkills.map((s: any) => (
                      <Checkbox key={s.id} checked={boundSkillIds.includes(s.id)}
                        onChange={() => toggleSkill(s.id)}>
                        <span style={{ fontSize: 12 }}>{s.name}</span>
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>
            ),
          },
        ]} />
      </Drawer>

      {/* Rename Session Modal */}
      <Modal
        title={t("session.rename")}
        open={!!renameSession}
        onOk={handleRenameSession}
        onCancel={() => setRenameSession(null)}
        okText="Save"
        width={380}
      >
        <Input
          value={renameTitle}
          onChange={(e) => setRenameTitle(e.target.value)}
          onPressEnter={handleRenameSession}
          autoFocus
        />
      </Modal>

      {/* Create Session Modal */}
      <Modal
        title={t("session.create_title")}
        open={!!createModalAgent}
        onOk={confirmCreateSession}
        onCancel={() => setCreateModalAgent(null)}
        okText="Create"
        width={420}
      >
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            Agent: {createModalAgent?.name}
          </span>
        </div>
        <Input
          value={newSessionTitle}
          onChange={(e) => setNewSessionTitle(e.target.value)}
          placeholder={t("session.name_placeholder")}
          autoFocus
          onPressEnter={confirmCreateSession}
        />
      </Modal>
    </div>
  );
}
